import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ResumeTemplates from './ResumeTemplates';
import ResumeEditor from './ResumeEditor';
import ResumeUploadModal from './ResumeUploadModal';
import Footer from '@/Company/About/Footer';
import Navbar from '@/Company/About/Navbar';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { Container } from '@/components/ui/container';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Menu, X, ChevronDown, FileText, CheckCircle, Star, Zap, Sparkles, Download, FileCheck, Lightbulb, TrendingUp, Users, Award, Shield, Cloud, Lock, Smartphone, Upload, FileUp, Clipboard, AlertCircle, Loader2, Sparkles as SparklesIcon } from 'lucide-react';
import './resume-landing.css';
import './ResumeLandingPage.css';

const SAVED_TEMPLATES_STORAGE_KEY = "savedCvTemplates";
// Removed ResumeSummaryGenerator and CoverLetterGenerator imports

const TESTIMONIALS = [
  { name: "Mythri", role: "Software Engineer", text: "This AI resume builder changed my career! The templates are absolutely fantastic and ATS-friendly. Got my dream job!" },
  { name: "Aman", role: "Data Scientist", text: "As a fresher, I was struggling to create a professional resume. This tool made it so easy and I landed my dream job!" },
  { name: "Anubhav", role: "Product Manager", text: "Best investment I made for my career. The AI suggestions helped me highlight skills I didn't even know were valuable. Highly recommend!" },
  { name: "Anushree", role: "UX Designer", text: "Outstanding tool! The templates are modern and the AI suggestions are spot-on. Worth every penny for career growth." },
  { name: "Mansi", role: "DevOps Engineer", text: "Perfect for career transitions. The AI helped me reframe my experience for a new industry. Now working at my dream company!" },
];

const ResumeLandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Initialize view from URL params immediately to prevent flash
  const getInitialView = () => {
    const urlParams = new URLSearchParams(location.search);
    const view = urlParams.get('view');
    const savedTemplateId = urlParams.get('savedTemplateId');
    
    if (savedTemplateId) {
      // Check if saved template exists
      try {
        const stored = localStorage.getItem(SAVED_TEMPLATES_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            const match = parsed.find((item) => item?.id === savedTemplateId);
            if (match && match.data) {
              return 'editor';
            }
          }
        }
      } catch (error) {
        console.error('Failed to check saved template', error);
      }
      return 'templates';
    }
    
    if (view === 'templates') {
      return 'templates';
    }
    
    return 'home';
  };
  
  const [currentView, setCurrentView] = useState(getInitialView()); // 'home', 'templates', 'editor'
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [parsedResumeData, setParsedResumeData] = useState(null);
  
  // JD Align Resume states
  const [showJdAlignModal, setShowJdAlignModal] = useState(false);
  const [jdFile, setJdFile] = useState(null);
  const [jdText, setJdText] = useState(''); // New state for JD text input
  const [jdInputMethod, setJdInputMethod] = useState('file'); // 'file' or 'text'
  const [useExistingResume, setUseExistingResume] = useState(true);
  const [uploadedResumeFile, setUploadedResumeFile] = useState(null);
  const [jdAlignStep, setJdAlignStep] = useState('options'); // 'options', 'processing', 'result'
  const [tailoredResumeData, setTailoredResumeData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  
  // JD Analysis states
  const [showAnalysisPopup, setShowAnalysisPopup] = useState(false);
  const [jdAnalysis, setJdAnalysis] = useState(null);
  const [jdData, setJdData] = useState(null);
  const [resumeData, setResumeData] = useState(null);
  const [activeFaqItem, setActiveFaqItem] = useState(null);
  const [autoDownloadRequested, setAutoDownloadRequested] = useState(false);

  const handleNavClick = (section) => {
    if (section === 'templates') {
      setShowUploadModal(true);
    } else if (section === 'jd-align') {
      setShowJdAlignModal(true);
    } else {
      setCurrentView(section);
    }
  };

  const handleFaqClick = (index) => {
    setActiveFaqItem(activeFaqItem === index ? null : index);
  };

  // Toggle Tools Dropdown

  // Check URL parameters on component mount and URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const view = urlParams.get('view');
    const modal = urlParams.get('modal');
    const savedTemplateId = urlParams.get('savedTemplateId');
    const shouldAutoDownload = urlParams.get('autoDownload') === 'true';

    if (savedTemplateId) {
      try {
        const stored = localStorage.getItem(SAVED_TEMPLATES_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            const match = parsed.find((item) => item?.id === savedTemplateId);
            if (match && match.data) {
              const snapshot = JSON.parse(JSON.stringify(match.data));
              snapshot.__savedTemplate = true;
              snapshot.__savedId = match.id;
              setSelectedTemplateId(
                match.templateId ||
                  snapshot.template_name ||
                  'professional'
              );
              setParsedResumeData(snapshot);
              setCurrentView('editor');
              setAutoDownloadRequested(shouldAutoDownload);
              return;
            }
          }
        }
      } catch (error) {
        console.error('Failed to load saved template', error);
      }
      // If saved template isn't found, fall back to templates view
      setAutoDownloadRequested(false);
      if (currentView !== 'templates') {
        setCurrentView('templates');
      }
      return;
    }
    
    if (view === 'templates') {
      // Only update view if it's not already set to templates (to prevent flash)
      if (currentView !== 'templates') {
      setCurrentView('templates');
      }
      setAutoDownloadRequested(false);
      // Check if we have parsed data from home page
      const savedParsedData = localStorage.getItem('parsedResumeData');
      if (savedParsedData) {
        try {
          const parsedData = JSON.parse(savedParsedData);
          setParsedResumeData(parsedData);
          // Clear the saved data after using it
          localStorage.removeItem('parsedResumeData');
        } catch (error) {
          // Error parsing saved resume data
        }
      }
    } else if (!view && !savedTemplateId && !modal) {
      // If no view parameter and we're on templates view, switch to home
      // This handles the case when back button removes the query param
      if (currentView === 'templates') {
        setCurrentView('home');
      }
    }
    
    if (modal === 'jd-align') {
      setShowJdAlignModal(true);
    }
  }, [location.search]);



  // Check Authentication
  const userEmail = localStorage.getItem("user_email");
  const companyEmail = localStorage.getItem("company_email");
  const isAuthenticated = userEmail || companyEmail;

  const handleCreateNew = () => {
    setShowUploadModal(false);
    setCurrentView('templates');
  };

  const handleUploadResume = (parsedData) => {
    setParsedResumeData(parsedData);
    setShowUploadModal(false);
    setCurrentView('templates');
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
  };

  const handleCloseJdAlignModal = () => {
    setShowJdAlignModal(false);
    setShowAnalysisPopup(false);
    setJdAlignStep('options');
    setJdFile(null);
    setJdText(''); // Reset JD text input
    setJdInputMethod('file'); // Reset to file input method
    setUseExistingResume(true);
    setUploadedResumeFile(null);
    setTailoredResumeData(null);
    setJdAnalysis(null);
    setJdData(null);
    setResumeData(null);
    setError('');
    setIsProcessing(false);
  };
    
  const handleTemplateSelect = (templateId, data) => {
    setAutoDownloadRequested(false);
    setSelectedTemplateId(templateId);
    setParsedResumeData(data); // Set the data that will be passed to ResumeEditor
    setCurrentView('editor');
  };

  // JD Align Resume handlers
  const handleJdFileChange = (event) => {
    const file = event.target.files[0];
    setJdFile(file);
    setError('');
  };

  const handleResumeFileChange = (event) => {
    const file = event.target.files[0];
    setUploadedResumeFile(file);
    setError('');
  };

  // Get existing resume data from localStorage
  const getExistingResumeData = () => {
    try {
      const savedData = localStorage.getItem('resumeData');
      if (savedData) {
        return JSON.parse(savedData);
      }
    } catch (error) {
      // Error reading saved resume data
    }
    return null;
  };

  // Save resume data to localStorage
  const saveResumeData = (data) => {
    try {
      localStorage.setItem('resumeData', JSON.stringify(data));
    } catch (error) {
      // Error saving resume data
    }
  };

  const handleJdAlignSubmit = async () => {
    if (!jdFile && !jdText) {
      setError('Please upload a JD file or enter JD text.');
      return;
    }

    if (!useExistingResume && !uploadedResumeFile) {
      setError('Please select an existing resume or upload a new one.');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      // Check if backend is running first
      const backendHealthy = await checkBackendHealth();
      if (!backendHealthy) {
        throw new Error('Backend server is not running. Please start the backend server on port 5001.');
      }

      let resumeData = null;

      if (useExistingResume) {
        // Use existing resume data from localStorage
        resumeData = getExistingResumeData();
        if (!resumeData) {
          throw new Error('No existing resume data found. Please upload a resume first.');
        }
      } else {
        // Process new resume file
        const newResumeData = await processNewResume(uploadedResumeFile);
        resumeData = newResumeData;
        // Save the new resume data
        saveResumeData(newResumeData);
      }

      // Ensure experience data is properly mapped before sending
      const processedResumeData = {
        ...resumeData,
        // Map experience to relevant_experience if it exists
        relevant_experience: resumeData?.relevant_experience || resumeData?.experience || [],
        // Ensure other fields are properly mapped
        experience: resumeData?.experience || resumeData?.relevant_experience || []
      };

      // Call backend to get AI analysis
      const formData = new FormData();
      
      // Add JD file or text
      if (jdFile) {
        formData.append('jd', jdFile);
      } else if (jdText) {
        formData.append('jd_text', jdText);
      }
      
      // Add resume data
      formData.append('resume_data', JSON.stringify(processedResumeData));
      
      // Call backend API to get analysis
      const response = await fetch('/api/create-tailored-resume', {
        method: 'POST',
        body: formData,
        mode: 'cors',
      });

      if (!response.ok) {
        const text = await response.text();
        let msg = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const json = JSON.parse(text);
          if (json?.error) msg += ` – ${json.error}`;
        } catch {}
        throw new Error(msg);
      }

      const tailoredData = await response.json();
      
      // Check if backend provided analysis
      if (tailoredData && tailoredData.jd_analysis) {
        // Use the AI analysis directly from backend
        const aiAnalysis = tailoredData.jd_analysis;
        
        // Convert backend format to frontend display format
        const frontendAnalysis = {
          matchScore: aiAnalysis.match_score || 0,
          matchedSkills: aiAnalysis.matched_skills || [],
          missingSkills: aiAnalysis.missing_skills || [],
          recommendations: aiAnalysis.recommendations || [],
          overallMatch: aiAnalysis.match_level || 'poor',
          totalJdSkills: aiAnalysis.total_jd_skills || 0,
          totalResumeSkills: aiAnalysis.total_resume_skills || 0,
          skillAnalysis: aiAnalysis.skill_analysis || {},
          detailedAnalysis: aiAnalysis.detailed_analysis || {}
        };
        
        // Store data for analysis popup
        setResumeData(processedResumeData);
        setJdAnalysis(frontendAnalysis);
        
        // Show analysis popup
        setShowAnalysisPopup(true);
      } else {
        setError('Backend analysis failed - please try again');
        setJdAlignStep('options');
        return;
      }
      
      setIsProcessing(false);
      
    } catch (error) {
      // Provide helpful error messages
      let errorMessage = error.message;
      if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Cannot connect to backend server. Please ensure the backend is running on port 5001.';
      } else if (error.message.includes('Backend error: 500')) {
        errorMessage = 'Backend server error. Please check the backend logs and try again.';
      } else if (error.message.includes('Backend error: 400')) {
        errorMessage = 'Invalid request. Please check your JD file format and try again.';
      }
      
      setError(errorMessage);
      setJdAlignStep('options');
    } finally {
      setIsProcessing(false);
    }
  };

  // Process new resume file
  const processNewResume = async (file) => {
    const formData = new FormData();
    formData.append('resume', file);

    const response = await fetch('/api/parse-resume', {
      method: 'POST',
      body: formData,
      mode: 'cors',
    });

    if (!response.ok) {
      throw new Error('Failed to process resume file');
    }

    const data = await response.json();
    return data;
  };

  // Function to clean HTML tags from text
  const cleanHtmlTags = (text) => {
    if (typeof text !== 'string') return text;
    return text.replace(/<[^>]*>/g, '');
  };

  // Process tailored data to match our form structure
  const processTailoredData = (data) => {
    // Handle the backend response structure properly
    const personalInfo = data.personal_info || {};
    const atsSections = data.ats_sections || {};

    const sanitizeText = (value) => {
      if (typeof value === 'string') {
        return value.trim();
      }
      if (value === null || value === undefined) {
        return '';
      }
      return String(value).trim();
    };

    const sanitizeArray = (items) => {
      if (!Array.isArray(items)) return [];
      return items
        .map((item) => sanitizeText(item))
        .filter((item) => item.length > 0);
    };

    const highlightSections = Array.isArray(atsSections.highlight_sections)
      ? atsSections.highlight_sections
          .map((section) => ({
            title: sanitizeText(section?.title),
            items: sanitizeArray(section?.items),
          }))
          .filter((section) => section.items.length > 0)
      : [];
    
    return {
      template_name: "professional",
      isTemplate: false,
      // Personal Information - handle both nested and flat structures
      full_name: personalInfo.full_name || data.full_name || "",
      email: personalInfo.email || data.email || "",
      phone: personalInfo.phone || data.phone || "",
      location: personalInfo.location || data.location || "",
      github: personalInfo.github || data.github || "",
      linkedin: personalInfo.linkedin || atsSections.linkedin || data.linkedin || "",
      leetcode: personalInfo.leetcode || data.leetcode || "",
      hackerrank: personalInfo.hackerrank || data.hackerrank || "",
      whatsapp: atsSections.whatsapp || personalInfo.whatsapp || "",
      
      // Professional Summary (AI optimized for JD)
      professional_summary: cleanHtmlTags(data.professional_summary || data.summary || data.resume_summary || ""),
      summary_intro: cleanHtmlTags(atsSections.summary_intro || data.professional_summary || data.summary || data.resume_summary || ""),
      looking_to_items: sanitizeArray(atsSections.looking_to_items),
      expertise_paragraph: cleanHtmlTags(atsSections.expertise_paragraph || ""),
      collaboration_paragraph: cleanHtmlTags(atsSections.collaboration_paragraph || ""),
      call_to_action_phone: sanitizeText(atsSections.call_to_action_phone),
      call_to_action_email: sanitizeText(atsSections.call_to_action_email),
      
      // Skills (prioritized based on JD requirements) - ensure proper comma formatting
      skills: {
        programming_languages: Array.isArray(data.skills?.programming_languages) ? data.skills.programming_languages : 
          (data.skills?.programming_languages || data.skills?.technical_skills || data.programming_languages || "").toString().split(/[,]/).map(s => s.trim()).filter(s => s),
        tools: Array.isArray(data.skills?.tools) ? data.skills.tools : 
          (data.skills?.tools || data.skills?.technologies || data.tools || "").toString().split(/[,]/).map(s => s.trim()).filter(s => s),
        frameworks: Array.isArray(data.skills?.frameworks) ? data.skills.frameworks : 
          (data.skills?.frameworks || data.frameworks || "").toString().split(/[,]/).map(s => s.trim()).filter(s => s),
        ai_ml_technologies: Array.isArray(data.skills?.ai_ml_technologies) ? data.skills.ai_ml_technologies : 
          (data.skills?.ai_ml_technologies || data.skills?.ai_technologies || data.ai_ml_technologies || "").toString().split(/[,]/).map(s => s.trim()).filter(s => s)
      },
      key_skills: Array.isArray(data.skills?.programming_languages) ? data.skills.programming_languages : 
        (data.skills?.programming_languages || data.skills?.technical_skills || data.programming_languages || "").toString().split(/[,]/).map(s => s.trim()).filter(s => s),
      technical_skills: Array.isArray(data.skills?.tools) ? data.skills.tools : 
        (data.skills?.tools || data.skills?.technologies || data.tools || "").toString().split(/[,]/).map(s => s.trim()).filter(s => s),
      key_bullet_sections: highlightSections.length
        ? highlightSections
        : (() => {
            const fallbackItems = (
              Array.isArray(data.skills?.programming_languages)
                ? data.skills.programming_languages
                : (data.skills?.programming_languages || data.skills?.technical_skills || data.programming_languages || "")
                    .toString()
                    .split(/[,]/)
                    .map((s) => s.trim())
                    .filter((s) => s)
            ).slice(0, 8);
            return fallbackItems.length
              ? [
                  {
                    title: "Key Highlights",
                    items: fallbackItems,
                  },
                ]
              : [];
          })(),
      
      // Experience (AI optimized for JD relevance)
      relevant_experience: Array.isArray(data.relevant_experience) ? data.relevant_experience : 
        (Array.isArray(data.experience) ? data.experience : []),
      experience: Array.isArray(data.experience) ? data.experience : 
        (Array.isArray(data.relevant_experience) ? data.relevant_experience : []),
      
      // Education
      education: Array.isArray(data.education) ? data.education : [],
      
      // Projects (AI optimized for JD relevance)
      projects: Array.isArray(data.projects) ? data.projects : [],
      
      // Other fields
      achievements: Array.isArray(data.achievements) ? data.achievements : [],
      certifications: Array.isArray(data.certifications) ? data.certifications : [],
      // Transform languages to strings for consistency
      languages: Array.isArray(data.languages) ? data.languages.map(lang => {
        if (typeof lang === 'string') return lang;
        if (typeof lang === 'object' && lang !== null) {
          const languageName = lang.language || lang.name || '';
          const proficiencyLevel = lang.proficiency || '';
          if (languageName && proficiencyLevel) {
            return `${languageName} (${proficiencyLevel})`;
          }
          return languageName || '';
        }
        return String(lang);
      }) : [],
      interests: Array.isArray(data.interests) ? data.interests : []
    };
  };

  // Normalize JD data to ensure it has the expected structure
  const normalizeJdData = (jdData) => {
    if (!jdData || typeof jdData !== 'object') {
      return {
        required_skills: ['communication', 'teamwork', 'problem solving'],
        optional_skills: ['leadership', 'project management'],
        soft_skills: ['adaptability', 'learning ability'],
        job_overview: 'Job description data unavailable',
        key_responsibilities: 'Various responsibilities as outlined in the job description',
        required_qualifications: 'Relevant qualifications and experience'
      };
    }

    // Ensure all required fields exist
    const normalized = {
      required_skills: jdData.required_skills || jdData.skills || [],
      optional_skills: jdData.optional_skills || [],
      soft_skills: jdData.soft_skills || [],
      job_overview: jdData.job_overview || jdData.description || jdData.overview || '',
      key_responsibilities: jdData.key_responsibilities || jdData.responsibilities || '',
      required_qualifications: jdData.required_qualifications || jdData.qualifications || ''
    };

    // Convert to arrays if they're strings
    if (typeof normalized.required_skills === 'string') {
      normalized.required_skills = [normalized.required_skills];
    }
    if (typeof normalized.optional_skills === 'string') {
      normalized.optional_skills = [normalized.optional_skills];
    }
    if (typeof normalized.soft_skills === 'string') {
      normalized.soft_skills = [normalized.soft_skills];
    }

    // Ensure arrays exist
    if (!Array.isArray(normalized.required_skills)) {
      normalized.required_skills = [];
    }
    if (!Array.isArray(normalized.optional_skills)) {
      normalized.optional_skills = [];
    }
    if (!Array.isArray(normalized.soft_skills)) {
      normalized.soft_skills = [];
    }

    return normalized;
  };

  // Analyze JD vs Resume compatibility
  // NOTE: This function is now DEPRECATED - we use backend Gemini AI analysis instead
  // Keeping for fallback only - should not be used in normal flow
  const analyzeJdVsResume = (jdData, resumeData) => {
    // Return fallback analysis - this should not be used in normal flow
    return {
      matchScore: 0,
      matchedSkills: [],
      missingSkills: [],
      recommendations: ['Backend AI analysis failed - using fallback data'],
      overallMatch: 'poor',
      totalJdSkills: 0,
      totalResumeSkills: 0
    };
  };

  // Extract skills from text using keyword matching
  // NOTE: This function is now DEPRECATED - backend Gemini AI handles skill extraction
  const extractSkillsFromText = (text) => {
    return []; // Return empty array since backend handles this
  };

  // Calculate similarity between two strings
  // NOTE: This function is now DEPRECATED - backend Gemini AI handles similarity calculations
  const calculateSimilarity = (str1, str2) => {
    return 0; // Return 0 since backend handles this
  };

  // Calculate edit distance between two strings
  // NOTE: This function is now DEPRECATED - backend Gemini AI handles similarity calculations
  const editDistance = (str1, str2) => {
    return 0; // Return 0 since backend handles this
  };

  // Check if backend is running
  const checkBackendHealth = async () => {
    try {
      const response = await fetch('/api/health', {
        method: 'GET',
        mode: 'cors',
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  // Upload and parse JD file
  const upload_jd = async (file) => {
    try {
      const formData = new FormData();
      formData.append('jd', file);

      const response = await fetch('/api/upload-jd', {
        method: 'POST',
        body: formData,
        mode: 'cors',
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Backend error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // If it's a network/connection error, provide fallback data
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        return {
          required_skills: ['communication', 'teamwork', 'problem solving'],
          optional_skills: ['leadership', 'project management'],
          soft_skills: ['adaptability', 'learning ability'],
          job_overview: 'JD file processing failed due to network issues. Using fallback analysis.',
          key_responsibilities: 'Various responsibilities as outlined in the job description',
          required_qualifications: 'Relevant qualifications and experience'
        };
      }
      
      throw error;
    }
  };

  // Parse JD text
  const parse_job_description = async (text) => {
    try {
      const response = await fetch('/api/parse-jd-text', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ jd_text: text }),
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error('Failed to parse JD text');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  };

  // Generate tailored resume after analysis
  const generateTailoredResume = async () => {
    setIsProcessing(true);
    setJdAlignStep('processing');

    try {
      const formData = new FormData();
      
      // Add JD file or text
      if (jdFile) {
        formData.append('jd', jdFile);
      } else if (jdText) {
        formData.append('jd_text', jdText);
      }
      
      // Add resume data
      formData.append('resume_data', JSON.stringify(resumeData));
      
      // Call backend API to generate tailored resume
      const response = await fetch('/api/create-tailored-resume', {
        method: 'POST',
        body: formData,
        mode: 'cors',
      });

      if (!response.ok) {
        const text = await response.text();
        let msg = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const json = JSON.parse(text);
          if (json?.error) msg += ` – ${json.error}`;
        } catch {}
        throw new Error(msg);
      }

      const tailoredData = await response.json();
      
      // Persist so Templates and Editor can read it
      localStorage.setItem('tailoredResumeData', JSON.stringify(tailoredData));
      localStorage.setItem('isJDOptimized', 'true');
      
      // Process and map the tailored data
      const processedData = processTailoredData(tailoredData);
      setTailoredResumeData(processedData);
      
      // Go to templates page with tailored data
      handleCloseJdAlignModal();
      setCurrentView('templates');
      
    } catch (error) {
      setError(error.message || 'Failed to generate tailored resume. Please try again.');
      setJdAlignStep('analysis');
    } finally {
      setIsProcessing(false);
    }
  };

  // Check backend connection on component mount
  useEffect(() => {
    const testBackendConnection = async () => {
      try {
        const isHealthy = await checkBackendHealth();
        if (!isHealthy) {
          // Backend server is not accessible
        } else {
          // Backend server is running
        }
      } catch (error) {
        // Backend connection test failed
      }
    };

    testBackendConnection();
  }, []);

  // --- Main Render Logic ---
  if (currentView === 'editor') {
    return (
      <ResumeEditor 
        templateId={selectedTemplateId || 'professional'}
        onBack={() => setCurrentView('templates')}
        resumeData={parsedResumeData}
        autoDownload={autoDownloadRequested}
        onAutoDownloadComplete={() => setAutoDownloadRequested(false)}
      />
    );
  }

  if (currentView === 'templates') {
    // Check if we have tailored data from JD alignment
    const tailored = JSON.parse(localStorage.getItem('tailoredResumeData') || 'null');
    const dataForTemplates = tailored || parsedResumeData || null;
    
    return (
      <ResumeTemplates 
        onBack={() => {
          // Navigate to home page without query params to avoid getting stuck
          // Use replace to avoid adding to history stack
          navigate("/create-resume", { replace: true });
          // Small delay to ensure navigation completes before setting view
          setTimeout(() => {
            setCurrentView('home');
          }, 50);
        }} 
        onTemplateSelect={handleTemplateSelect}
        parsedData={dataForTemplates}
      />
    );
  }

  // Default view (home)
  return (
    <div className="min-h-screen bg-background text-foreground w-full">
      <Navbar />
      
      {/* Top Feature Bar */}
      <div className="bg-primary text-primary-foreground py-2 border-b border-primary/20">
        <Container>
          <div className="flex items-center justify-center gap-4 flex-wrap text-sm font-medium">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span>Professional Templates</span>
            </div>
            <Separator orientation="vertical" className="h-4 bg-primary-foreground/30" />
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              <span>ATS-Optimized</span>
            </div>
            <Separator orientation="vertical" className="h-4 bg-primary-foreground/30" />
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <span>AI Writing Assistant</span>
            </div>
            <Separator orientation="vertical" className="h-4 bg-primary-foreground/30" />
            <div className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              <span>1-Click Export</span>
            </div>
          </div>
        </Container>
      </div>

      <main className="w-full">
        {currentView === 'home' && (
          <div className="w-full">
            {/* AI-Powered Badge */}
            <div className="flex justify-center mt-8 mb-6">
              <Badge variant="default" className="gap-2 px-6 py-2.5 text-sm font-semibold bg-primary text-primary-foreground shadow-md hover:shadow-lg transition-shadow duration-300">
                <Zap className="h-4 w-4" />
                AI-Powered Resume Builder
              </Badge>
            </div>


            {/* Enhanced Hero Section */}
            <section className="py-16 md:py-24 bg-background relative overflow-hidden">
              <Container>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div className="space-y-6">
                    <div className="relative">
                      <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-foreground">
                        The <span className="text-primary">Resume</span> That Lands Your <span className="text-primary">Dream Job</span>
                      </h1>
                    </div>
                    <p className="text-xl text-foreground/80">
                      Create an impressive resume in minutes. 
                    </p>
                    <div className="flex gap-4 flex-wrap">
                      <Button 
                        size="lg"
                        onClick={() => handleNavClick('templates')}
                        className="text-base bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg"
                      >
                        Create My Resume
                      </Button>
                      <Button 
                        variant="outline"
                        size="lg"
                        onClick={() => setCurrentView('templates')}
                        className="text-base border-2 border-foreground/20 hover:border-foreground/40"
                      >
                        View Templates
                      </Button>
                    </div>
                  </div>
                  
                  <div className="relative hidden lg:block">
                    <div className="relative">
                      {/* Resume Template 1 - Left, Tilted */}
                      <div className="absolute left-0 top-8 transform -rotate-6 z-10 hover:scale-[1.02] transition-transform duration-500 ease-out">
                        <div className="relative">
                          <Card className="overflow-hidden shadow-xl w-48 hover:shadow-2xl transition-shadow duration-300">
                            <img src="/image-1.jpg" alt="Resume Template 1" className="w-full h-auto" />
                          </Card>
                          <Badge className="absolute -top-2 -right-2 rotate-12 bg-primary text-primary-foreground shadow-md px-2 py-1 text-xs font-bold z-20">
                            ATS
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Resume Template 2 - Center, Straight */}
                      <div className="absolute left-1/2 top-0 transform -translate-x-1/2 z-20 hover:scale-[1.02] transition-transform duration-500 ease-out">
                        <Card className="overflow-hidden shadow-xl w-48 hover:shadow-2xl transition-shadow duration-300">
                          <img src="/image-2.jpg" alt="Resume Template 2" className="w-full h-auto" />
                        </Card>
                      </div>
                      
                      {/* Resume Template 3 - Right, Tilted */}
                      <div className="absolute right-0 top-8 transform rotate-6 z-10 hover:scale-[1.02] transition-transform duration-500 ease-out">
                        <Card className="overflow-hidden shadow-xl w-48 hover:shadow-2xl transition-shadow duration-300">
                          <img src="/image-3.jpg" alt="Resume Template 3" className="w-full h-auto" />
                        </Card>
                      </div>
                      
                      {/* Spacer to maintain height */}
                      <div className="h-96"></div>
                    </div>
                  </div>
                </div>
              </Container>
            </section>

            {/* Company Logos Section */}
            <section className="py-12 border-t border-b border-border bg-background w-full">
              <Container>
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <Star className="h-5 w-5 text-primary" />
                    <h3 className="text-2xl font-bold text-foreground">
                      Our clients are employed by:
                    </h3>
                  </div>
                </div>
              </Container>
              <div className="resume-companies-logos-carousel w-full">
                <div className="resume-companies-logos-track">
                  {/* Multiple sets for seamless infinite circular queue */}
                  <img src="/nomiso.png" alt="Nomiso" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/isource.png" alt="iSource" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/indium.png" alt="Indium Tech" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/grandgaming.png" alt="Grand Gaming" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/kinzy.png" alt="Kinzy Club" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  {/* Duplicated sets for seamless infinite scroll */}
                  <img src="/nomiso.png" alt="Nomiso" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/isource.png" alt="iSource" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/indium.png" alt="Indium Tech" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/grandgaming.png" alt="Grand Gaming" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/kinzy.png" alt="Kinzy Club" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/nomiso.png" alt="Nomiso" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/isource.png" alt="iSource" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/indium.png" alt="Indium Tech" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/grandgaming.png" alt="Grand Gaming" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                  <img src="/kinzy.png" alt="Kinzy Club" className="resume-company-logo-item grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300" />
                </div>
              </div>
            </section>

            {/* AI Features Section */}
            <section className="py-16 bg-background">
              <Container>
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
                  AI-Powered Features
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card className="text-center hover:shadow-md transition-shadow duration-300">
                    <CardContent className="pt-6">
                      <div className="flex justify-center mb-4">
                        <div className="p-3 rounded-full bg-primary/10 hover:bg-primary/15 transition-colors duration-300">
                          <FileText className="h-6 w-6 text-primary" />
                        </div>
                      </div>
                      <CardTitle className="text-lg mb-2">Smart Content Generation</CardTitle>
                      <CardDescription>
                        AI writes compelling bullet points and descriptions tailored to your industry
                      </CardDescription>
                    </CardContent>
                  </Card>
                  <Card className="text-center hover:shadow-md transition-shadow duration-300">
                    <CardContent className="pt-6">
                      <div className="flex justify-center mb-4">
                        <div className="p-3 rounded-full bg-primary/10 hover:bg-primary/15 transition-colors duration-300">
                          <CheckCircle className="h-6 w-6 text-primary" />
                        </div>
                      </div>
                      <CardTitle className="text-lg mb-2">ATS Optimization</CardTitle>
                      <CardDescription>
                        Automatically optimize your resume to pass Applicant Tracking Systems
                      </CardDescription>
                    </CardContent>
                  </Card>
                  <Card className="text-center hover:shadow-md transition-shadow duration-300">
                    <CardContent className="pt-6">
                      <div className="flex justify-center mb-4">
                        <div className="p-3 rounded-full bg-primary/10 hover:bg-primary/15 transition-colors duration-300">
                          <TrendingUp className="h-6 w-6 text-primary" />
                        </div>
                      </div>
                      <CardTitle className="text-lg mb-2">Match Analysis</CardTitle>
                      <CardDescription>
                        Get detailed insights on how well your resume matches job requirements
                      </CardDescription>
                    </CardContent>
                  </Card>
                  <Card className="text-center hover:shadow-md transition-shadow duration-300">
                    <CardContent className="pt-6">
                      <div className="flex justify-center mb-4">
                        <div className="p-3 rounded-full bg-primary/10 hover:bg-primary/15 transition-colors duration-300">
                          <Zap className="h-6 w-6 text-primary" />
                        </div>
                      </div>
                      <CardTitle className="text-lg mb-2">Instant Updates</CardTitle>
                      <CardDescription>
                        Real-time suggestions
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>
              </Container>
            </section>

            {/* Key Benefits Section */}
            <section className="py-16 bg-background">
              <Container>
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
                  Why Choose Pro Resume Builder?
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Card className="relative hover:shadow-lg hover:scale-105 transition-all duration-300">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="p-3 rounded-lg bg-primary/10 group hover:bg-primary/20 transition-colors">
                          <FileText className="h-6 w-6 text-primary group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300" />
                        </div>
                        <Badge variant="secondary">New</Badge>
                      </div>
                      <CardTitle className="mb-2">Professional Templates</CardTitle>
                      <CardDescription>
                        Choose from expertly designed templates that catch recruiters' attention
                      </CardDescription>
                    </CardContent>
                  </Card>
                  <Card className="relative hover:shadow-lg hover:scale-105 transition-all duration-300">
                    <CardContent className="pt-6">
                      <div className="flex items-start mb-4">
                        <div className="p-3 rounded-lg bg-primary/10 group hover:bg-primary/20 transition-colors">
                          <Zap className="h-6 w-6 text-primary group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300" />
                        </div>
                      </div>
                      <CardTitle className="mb-2">Quick & Easy</CardTitle>
                      <CardDescription>
                        Build your resume in minutes with our intuitive drag-and-drop editor
                      </CardDescription>
                    </CardContent>
                  </Card>
                  <Card className="relative hover:shadow-lg hover:scale-105 transition-all duration-300">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="p-3 rounded-lg bg-primary/10 group hover:bg-primary/20 transition-colors">
                          <Shield className="h-6 w-6 text-primary group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300" />
                        </div>
                        <Badge variant="secondary">New</Badge>
                      </div>
                      <CardTitle className="mb-2">ATS Friendly</CardTitle>
                      <CardDescription>
                        All templates are optimized to pass Applicant Tracking Systems
                      </CardDescription>
                    </CardContent>
                  </Card>
                  <Card className="relative hover:shadow-lg hover:scale-105 transition-all duration-300">
                    <CardContent className="pt-6">
                      <div className="flex items-start mb-4">
                        <div className="p-3 rounded-lg bg-primary/10 group hover:bg-primary/20 transition-colors">
                          <Smartphone className="h-6 w-6 text-primary group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300" />
                        </div>
                      </div>
                      <CardTitle className="mb-2">Mobile Responsive</CardTitle>
                      <CardDescription>
                        Edit and view your resume on any device, anywhere, anytime
                      </CardDescription>
                    </CardContent>
                  </Card>
                  <Card className="relative hover:shadow-lg hover:scale-105 transition-all duration-300">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="p-3 rounded-lg bg-primary/10 group hover:bg-primary/20 transition-colors">
                          <Cloud className="h-6 w-6 text-primary group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300" />
                        </div>
                        <Badge variant="secondary">New</Badge>
                      </div>
                      <CardTitle className="mb-2">Cloud Storage</CardTitle>
                      <CardDescription>
                        Your resumes are safely stored and accessible from anywhere
                      </CardDescription>
                    </CardContent>
                  </Card>
                  <Card className="relative hover:shadow-lg hover:scale-105 transition-all duration-300">
                    <CardContent className="pt-6">
                      <div className="flex items-start mb-4">
                        <div className="p-3 rounded-lg bg-primary/10 group hover:bg-primary/20 transition-colors">
                          <Lock className="h-6 w-6 text-primary group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300" />
                        </div>
                      </div>
                      <CardTitle className="mb-2">Secure & Private</CardTitle>
                      <CardDescription>
                        Your data is encrypted.
                      </CardDescription>
                    </CardContent>
                  </Card>
                </div>
              </Container>
            </section>

            {/* Statistics Section */}
            <section className="py-16 bg-background">
              <Container>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="bg-card border-2 border-primary/20 hover:border-primary/40 transition-colors">
                    <CardContent className="pt-6 text-center">
                      <div className="flex justify-center mb-4">
                        <Star className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-3xl font-bold mb-2 text-foreground">5/5</h3>
                      <p className="text-muted-foreground">User Rating</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-2 border-primary/20 hover:border-primary/40 transition-colors">
                    <CardContent className="pt-6 text-center">
                      <div className="flex justify-center mb-4">
                        <Users className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-3xl font-bold mb-2 text-foreground">85%</h3>
                      <p className="text-muted-foreground">changes to get job interviews</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-card border-2 border-primary/20 hover:border-primary/40 transition-colors">
                    <CardContent className="pt-6 text-center">
                      <div className="flex justify-center mb-4">
                        <Award className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-3xl font-bold mb-2 text-foreground">98%</h3>
                      <p className="text-muted-foreground">Recommendation Rate</p>
                    </CardContent>
                  </Card>
                </div>
              </Container>
            </section>
            {/* Powered Resume Builder Section */}
            <section className="py-16 bg-background overflow-hidden">
              <Container>
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
                  Powered Resume Builder
                </h2>
                <div className="resume-testimonials-carousel w-full">
                  <div className="resume-testimonials-track">
                    {/* Render testimonials three times for seamless infinite scroll */}
                    {[0, 1, 2].map((setIndex) =>
                      TESTIMONIALS.map((testimonial, idx) => (
                        <Card key={`set-${setIndex}-${idx}`} className="resume-testimonial-card">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Avatar className="h-8 w-8 ring-1 ring-primary/20">
                                <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                                  {testimonial.name[0]}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm truncate">{testimonial.name}</div>
                                <div className="text-xs text-muted-foreground truncate">{testimonial.role}</div>
                              </div>
                            </div>
                            <div className="mb-2 flex gap-0.5">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className="h-3 w-3 text-primary fill-primary" />
                              ))}
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-3 mb-2">{testimonial.text}</p>
                            <Badge variant="outline" className="text-xs px-1.5 py-0.5">India</Badge>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              </Container>
            </section>

            {/* FAQ Section */}
            <section className="py-16 bg-background">
              <Container>
                <div className="max-w-3xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-foreground">
                    Frequently Asked Questions about <span className="text-primary">Pro Resume Builder</span>
                  </h2>
                  <p className="text-center text-muted-foreground mb-8">
                    Everything you need to know about our AI-powered resume building platform.
                  </p>
                  
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="item-1">
                      <AccordionTrigger className="text-left">
                        <div className="flex items-center gap-2">
                          <Star className="h-4 w-4 text-primary" />
                          How does our Pro Resume Builder work?
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-muted-foreground">
                          Our free Resume Builder uses advanced AI technology to help you create professional resumes in minutes. Simply choose a template, input your information, and our AI will suggest improvements, optimize content for ATS systems, and generate a polished resume ready for download in multiple formats.
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="item-2">
                      <AccordionTrigger className="text-left">
                        <div className="flex items-center gap-2">
                          <Star className="h-4 w-4 text-primary" />
                          What makes Pro Resume Builder?
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-muted-foreground">
                          Pro Resume Builder stands out with its AI-powered content generation, ATS optimization, real-time suggestions, and professional templates. Unlike basic builders, we provide intelligent recommendations, industry-specific formatting, and advanced features that help you create resumes that actually get noticed by employers.
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="item-3">
                      <AccordionTrigger className="text-left">
                        <div className="flex items-center gap-2">
                          <Star className="h-4 w-4 text-primary" />
                          Can I use this resume builder for different career levels?
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-muted-foreground">
                          Absolutely! Our AI-powered resume builder is designed for professionals at all career levels - from fresh graduates and entry-level candidates to senior executives and C-level professionals. The AI adapts its suggestions based on your experience level, industry, and career goals. Whether you're just starting out or looking to advance to the next level, our templates and AI recommendations will help you create a compelling resume.
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </Container>
            </section>


            {/* CTA Section */}
            <section className="py-16 bg-background">
              <Container>
                <div className="text-center max-w-2xl mx-auto">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">Ready to Get Started?</h2>
                  <p className="text-muted-foreground mb-8">Join thousands of professionals who have built their resumes with us</p>
                  <Button 
                    size="lg"
                    onClick={() => handleNavClick('templates')}
                    className="text-base"
                  >
                    Start Building Now
                  </Button>
                </div>
              </Container>
            </section>
          </div>
        )}
      </main>
      
      {/* JD Align Resume Modal */}
      <Dialog open={showJdAlignModal} onOpenChange={setShowJdAlignModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0">
          <DialogHeader className="px-6 pt-5 pb-4 border-b">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <SparklesIcon className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-xl font-semibold">Tailor Resume to JD</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground mt-1">
                  Generate a customized resume aligned with job requirements.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          {jdAlignStep === 'options' && (
            <div className="px-6 py-4 space-y-4">
              {error && (
                <Alert variant="destructive" className="py-2.5">
                  <AlertCircle className="h-3.5 w-3.5" />
                  <AlertDescription className="text-xs">{error}</AlertDescription>
                </Alert>
              )}
              
              {/* Job Description Input Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <span className="text-xs font-semibold text-primary-foreground">1</span>
                  </div>
                  <Label className="text-sm font-semibold">Job Description</Label>
                </div>
                
                <Tabs
                  value={jdInputMethod}
                  onValueChange={(value) => value && setJdInputMethod(value)}
                  className="w-full"
                >
                  <TabsList className="grid w-full grid-cols-2 h-9">
                    <TabsTrigger value="file" className="text-xs">
                      <FileUp className="h-3.5 w-3.5 mr-1.5" />
                      Upload
                    </TabsTrigger>
                    <TabsTrigger value="text" className="text-xs">
                      <Clipboard className="h-3.5 w-3.5 mr-1.5" />
                      Paste
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="file" className="mt-3">
                    <input
                      type="file"
                      id="jd-file"
                      accept=".pdf,.docx,.txt"
                      onChange={handleJdFileChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('jd-file').click()}
                      className="w-full h-auto py-3 border-dashed hover:bg-accent/50"
                    >
                      <div className="flex flex-col items-center gap-1.5">
                        {jdFile ? (
                          <>
                            <FileCheck className="h-5 w-5 text-primary" />
                            <span className="text-xs font-medium">{jdFile.name}</span>
                          </>
                        ) : (
                          <>
                            <Upload className="h-5 w-5 text-muted-foreground" />
                            <span className="text-xs">Click to upload</span>
                            <span className="text-[10px] text-muted-foreground">PDF, DOCX, TXT</span>
                          </>
                        )}
                      </div>
                    </Button>
                  </TabsContent>
                  
                  <TabsContent value="text" className="mt-3">
                    <Textarea
                      rows={5}
                      placeholder="Paste job description here..."
                      value={jdText}
                      onChange={(e) => setJdText(e.target.value)}
                      className="resize-none text-sm min-h-[120px]"
                    />
                  </TabsContent>
                </Tabs>
              </div>

              <Separator />

              {/* Resume Source Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <span className="text-xs font-semibold text-primary-foreground">2</span>
                  </div>
                  <Label className="text-sm font-semibold">Resume Source</Label>
                </div>
                
                <RadioGroup 
                  value={useExistingResume ? "existing" : "new"} 
                  onValueChange={(value) => setUseExistingResume(value === "existing")}
                  className="space-y-2"
                >
                  <div className="flex items-center space-x-2.5">
                    <RadioGroupItem value="existing" id="resume-existing" className="h-4 w-4" />
                    <Label htmlFor="resume-existing" className="flex items-center gap-2 cursor-pointer font-normal text-xs">
                      <FileCheck className="h-3.5 w-3.5 text-muted-foreground" />
                      Use Existing
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2.5">
                    <RadioGroupItem value="new" id="resume-new" className="h-4 w-4" />
                    <Label htmlFor="resume-new" className="flex items-center gap-2 cursor-pointer font-normal text-xs">
                      <Upload className="h-3.5 w-3.5 text-muted-foreground" />
                      Upload New
                    </Label>
                  </div>
                </RadioGroup>
                
                {useExistingResume && (
                  <div className="p-2.5 rounded-md bg-muted/50 border text-xs">
                    <div className="flex items-center gap-2">
                      {getExistingResumeData() ? (
                        <>
                          <CheckCircle className="h-3.5 w-3.5 text-green-600 shrink-0" />
                          <span className="text-muted-foreground">Resume data found</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                          <span className="text-muted-foreground">No resume data found</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Upload Resume Section (conditional) */}
              {!useExistingResume && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0">
                        <span className="text-xs font-semibold text-primary-foreground">3</span>
                      </div>
                      <Label className="text-sm font-semibold">Upload Resume</Label>
                    </div>
                    <input
                      type="file"
                      id="resume-file"
                      accept=".pdf,.docx,.txt"
                      onChange={handleResumeFileChange}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('resume-file').click()}
                      className="w-full h-auto py-3 border-dashed hover:bg-accent/50"
                    >
                      <div className="flex flex-col items-center gap-1.5">
                        {uploadedResumeFile ? (
                          <>
                            <FileCheck className="h-5 w-5 text-primary" />
                            <span className="text-xs font-medium">{uploadedResumeFile.name}</span>
                          </>
                        ) : (
                          <>
                            <Upload className="h-5 w-5 text-muted-foreground" />
                            <span className="text-xs">Click to upload</span>
                            <span className="text-[10px] text-muted-foreground">PDF, DOCX, TXT</span>
                          </>
                        )}
                      </div>
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {jdAlignStep === 'processing' && (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Loader2 className="h-8 w-8 text-primary animate-spin" />
              </div>
              <h2 className="text-lg font-semibold mb-2">Tailoring Your Resume</h2>
              <p className="text-xs text-muted-foreground text-center max-w-xs">
                Analyzing job description and optimizing your resume...
              </p>
            </div>
          )}

          {jdAlignStep === 'options' && (
            <DialogFooter className="px-6 py-3.5 border-t bg-muted/30">
              <Button 
                size="default"
                onClick={handleJdAlignSubmit}
                disabled={(jdInputMethod === 'file' && !jdFile) || (jdInputMethod === 'text' && !jdText.trim()) || (!useExistingResume && !uploadedResumeFile) || isProcessing}
                className="w-full h-9 text-sm font-medium"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <SparklesIcon className="mr-2 h-3.5 w-3.5" />
                    Generate Tailored Resume
                  </>
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      
      {/* JD Analysis Popup Modal */}
      <Dialog open={showAnalysisPopup} onOpenChange={setShowAnalysisPopup}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Resume Match Analysis</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground mt-2">
              See how well your resume aligns with the job requirements and discover areas to improve.
            </DialogDescription>
          </DialogHeader>
          
          {jdAnalysis && (
            <div className="space-y-4">
              {/* Simple Score Display */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-center gap-6">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-primary mb-1.5">
                        {jdAnalysis.matchScore || 0}%
                      </div>
                      <div className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Match Score</div>
                    </div>
                    <Separator orientation="vertical" className="h-12" />
                    <div>
                      <Badge 
                        variant={jdAnalysis.overallMatch === 'excellent' ? 'success' : 
                                jdAnalysis.overallMatch === 'good' ? 'default' : 
                                jdAnalysis.overallMatch === 'fair' ? 'warning' : 'destructive'}
                        className="text-sm px-3 py-1.5 font-medium"
                      >
                        {jdAnalysis.overallMatch ? jdAnalysis.overallMatch.toUpperCase() : 'POOR'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Skills Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Skills You Have Section */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Matching Skills ({jdAnalysis.matchedSkills.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-2">
                      {jdAnalysis.matchedSkills.length > 0 ? (
                        jdAnalysis.matchedSkills.map((skill, index) => (
                          <Badge key={index} variant="success" className="text-xs">
                            {skill}
                          </Badge>
                        ))
                      ) : (
                        <p className="text-xs text-muted-foreground">No matching skills found</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Skills You Need Section */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold">Missing Skills ({jdAnalysis.missingSkills.length})</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-2">
                      {jdAnalysis.missingSkills.length > 0 ? (
                        jdAnalysis.missingSkills.map((skill, index) => (
                          <Badge key={index} variant="destructive" className="text-xs">
                            {skill}
                          </Badge>
                        ))
                      ) : (
                        <p className="text-xs text-muted-foreground">All required skills are covered!</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Analysis */}
              {jdAnalysis.detailedAnalysis && (
                <div className="space-y-4">
                  {/* Strengths and Areas to Improve in Two Columns */}
                  {(jdAnalysis.detailedAnalysis.strengths?.length > 0 || jdAnalysis.detailedAnalysis.gaps?.length > 0) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {jdAnalysis.detailedAnalysis.strengths && jdAnalysis.detailedAnalysis.strengths.length > 0 && (
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                              <TrendingUp className="h-4 w-4 text-primary" />
                              Strengths
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <ul className="list-disc list-inside space-y-1.5 text-muted-foreground text-xs">
                              {jdAnalysis.detailedAnalysis.strengths.map((strength, index) => (
                                <li key={index}>{strength}</li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      )}
                      
                      {jdAnalysis.detailedAnalysis.gaps && jdAnalysis.detailedAnalysis.gaps.length > 0 && (
                        <Card>
                          <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                              <Lightbulb className="h-4 w-4 text-warning" />
                              Areas to Improve
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <ul className="list-disc list-inside space-y-1.5 text-muted-foreground text-xs">
                              {jdAnalysis.detailedAnalysis.gaps.map((gap, index) => (
                                <li key={index}>{gap}</li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                  
                </div>
              )}

              {/* Recommendations and Focus Areas in Two Columns */}
              {(jdAnalysis.recommendations.length > 0 || (jdAnalysis.detailedAnalysis?.improvement_areas && jdAnalysis.detailedAnalysis.improvement_areas.length > 0)) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Recommendations */}
                  {jdAnalysis.recommendations.length > 0 && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                          <Lightbulb className="h-4 w-4 text-primary" />
                          Recommendations
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <ul className="list-disc list-inside space-y-1.5 text-muted-foreground text-xs">
                          {jdAnalysis.recommendations.slice(0, 3).map((rec, index) => (
                            <li key={index}>{rec}</li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                  
                  {/* Focus Areas */}
                  {jdAnalysis.detailedAnalysis?.improvement_areas && jdAnalysis.detailedAnalysis.improvement_areas.length > 0 && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                          <Award className="h-4 w-4 text-primary" />
                          Focus Areas
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <ul className="list-disc list-inside space-y-1.5 text-muted-foreground text-xs">
                          {jdAnalysis.detailedAnalysis.improvement_areas.map((area, index) => (
                            <li key={index}>{area}</li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <DialogFooter>
            <Button 
              variant="outline"
              onClick={() => {
                setShowAnalysisPopup(false);
                setJdAlignStep('options');
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={generateTailoredResume}
              disabled={isProcessing}
            >
              {isProcessing ? 'Generating...' : 'Get Tailored Resume'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {showUploadModal && (
        <ResumeUploadModal 
          onUpload={handleUploadResume}
          onCreateNew={handleCreateNew}
          onClose={handleCloseModal}
        />
      )}

      {/* Footer */}
      <div className="mt-16">
        <Footer />
      </div>
    </div>
  );
};

export default ResumeLandingPage;
